import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardProps,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { ItemImageArea } from 'utoniq-ui'

import { MissionBoxFragment } from '@/clients/utoniq-core/schema'
import { MissionLabel } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'

export type MissionBoxProps = {
  artistId?: string
  mission: MissionBoxFragment
  fullWidth?: boolean
  sx?: CardProps['sx']
  withArtistName?: boolean
  onClick?: () => void
}

export const MissionBox = ({
  artistId,
  mission,
  sx,
  fullWidth,
  withArtistName,
  onClick,
}: MissionBoxProps) => {
  const { generatePath, routerPages } = useRouter()
  const { t } = useTranslation('missionDetail')

  return (
    <Card
      elevation={0}
      sx={{
        mt: 3,
        position: 'relative',
        width: fullWidth ? '100%' : 315,
        overflow: 'visible',
        borderRadius: 2,
        backgroundImage: 'none',
        bgcolor: 'grey.50',
        ...sx,
      }}
    >
      <MissionLabel
        label={mission.label ?? null}
        sx={{
          position: 'absolute',
          top: -10,
          left: 20,
          zIndex: 1,
          width: 72,
          height: 20,
        }}
      />
      <CardActionArea
        sx={{
          p: 5,
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          borderRadius: 2,
        }}
        href={
          artistId
            ? generatePath(routerPages.ArtistMissionDetail, {
                missionId: mission?.id,
                artistId,
              })
            : generatePath(routerPages.MissionDetail, {
                missionId: mission?.id,
              })
        }
        onClick={onClick}
      >
        <CardContent
          component={Stack}
          flex={1}
          sx={{
            p: 0,
            pr: 4,
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1}>
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              variant="h5"
            >
              {mission.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-all', ...styleSets.lineCamp(1) }}
            >
              {mission.description}
            </Typography>
            <Box sx={{ pt: 1 }}>
              <Chip
                label={t(`${mission?.type}`)}
                size="small"
                sx={{
                  borderRadius: 1,
                  typography: 'overline',
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                }}
              />
              {mission.gpsData && (
                <Chip
                  label="CHECK-IN"
                  size="small"
                  sx={{
                    ml: 2,
                    borderRadius: 1,
                    typography: 'overline',
                    bgcolor: 'warning.dark',
                    color: 'white',
                  }}
                />
              )}
            </Box>
          </Stack>
          {withArtistName && (
            <Typography variant="subtitle2" align="right" color="#798a94">
              {mission.artist?.displayName}
            </Typography>
          )}
        </CardContent>
        <ItemImageArea
          size="x-small"
          src={
            mission?.reward?.item?.item?.thumbnail.front?.smallUri ||
            mission?.reward?.gachaTicket?.gachaTicket.thumbnails?.reward
              ?.smallUri ||
            ''
          }
        />
      </CardActionArea>
    </Card>
  )
}
